<style scoped lang="scss">
  form {
      h2 {
          color: #000;
      }
      ::v-deep(.formkit-select-icon) {
          height: 50px;
          margin: 0;
          padding: 0;
      }
  }
  main .container .card {
    background-color: rgba(91, 91, 98, 0.08);
  }
</style>
<template>
  <div class="mb-5">
    <h1 class="page-title">{{ title }}</h1>
  </div>
  <div class="max-w-screen-xl mx-auto">
    <div class="flex flex-wrap -mx-4">
      <div class="w-full lg:w-1/4 px-3">
        <div class="mb-5">
          <div class="card w-75 shadow-xl" v-if="!form.parentValue || form.parentValue?.lvl === 0">
            <div class="card-body items-center text-center">
              <h5 class="card-title">Miniature</h5>
              <UploadImage :image-preview-url="form.vignette ? form.vignette.url : getBaseUrl() + '/media/blank2.png'"
                  @image-removed="onImageRemoved"
                  @imageUploaded="onImageUpload"
                  @error="onImageUploadError"
              />
            </div>
              <div class="card-body items-center text-center">
                  <h5 class="card-title">Icône 3d</h5>
                  <UploadImage :image-preview-url="form.icon3d ? form.icon3d.url : getBaseUrl() + '/media/blank2.png'"
                               @image-removed="onImageRemoved3d"
                               @imageUploaded="onImageUpload3d"
                               @error="onImageUploadError"
                               :accepted-extensions="['.png']"
                  />
              </div>
          </div>
        </div>
      </div>

      <div class="w-full lg:w-3/4 px-4">
        <div class="card w-full shadow-xl">
          <div class="card-body">
            <h5 class="card-title">Général</h5>
            <div class="w-full">
              <FormKit type="form"
                :actions="false"
                :model="form"
                @submit="onSubmit"
                #default="{ state: { valid } }"
                outer-class="w-full"
                incomplete-message="Veuillez remplir tous les champs correctement"
              >
                <div class="mt-2">
                  <div class="flex">
                    <div class="mr-3"><label for="status">Statut</label></div>
                    <div>
                      <span :class="{
                        'badge': true,
                        'badge-error': !form.enabled,
                        'badge-success': form.enabled,
                        'badge-circle': true
                      }"></span>
                    </div>
                  </div>
                  <FormKit
                    type="select"
                    name="status"
                    v-model="form.enabled"
                    help="Faire apparaitre ou non la catégorie dans le catalogue"
                    :options="[
                      { label: 'Activé', value: true },
                      { label: 'Désactivé', value: false },
                    ]"
                  />
                </div>

                <FormKit type="autocomplete" 
                  name="parentValue"
                  v-model="form.parentValue"
                  label="Parent *"
                  placeholder="Choisissez une catégorie parente" 
                  validation="required"
                  :options="parents"
                  :validation-messages="{
                    required: 'La catégorie parente est obligatoire',
                  }"
                />
                <FormKit type="text"
                  name="slug"
                  v-model="form.slug"
                  label="Slug *"
                  validation="required | matches:/^[^\s][a-zA-Z0-9-_/%]*$/"
                  :validation-messages="{
                    required: 'Le slug est obligatoire',
                    matches: 'Le slug ne doit contenir que des lettres, des chiffres, des tirets et des slashs et ne doit pas commencer par un espace',
                  }"
                />
                <FormKit type="text"
                  name="name"
                  label="Nom *"
                  v-model="form.name"
                  validation="required | length:1,30"
                  :validation-messages="{
                    required: 'Le nom est obligatoire',
                    length: 'Le nom ne doit pas dépasser 30 caractères',
                  }"
                />
                <FormKit type="textarea"
                  name="description"
                  v-model="form.description"
                  label="Description"
                  validation="length:0,255"
                  :validation-messages="{
                    length: 'La description ne doit pas dépasser 255 caractères',
                  }"
                />
                <div class="card-actions">
                  <div v-if="loading === false">
                    <FormKit type="submit"
                      :label="submitBtnLabel"
                      :disabled="!valid"
                      :class="{ 'btn-primary': true, 'btn-block': true }"
                    />
                  </div>
                  <FormKit type="button"
                    label="Annuler"
                    @click="onCancel"
                    :class="{ 'btn-secondary': true, 'btn-block': true }"
                  />
                </div>
              </FormKit>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {onMounted, ref} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import ApiService from "@/_services/api.service";
import swal from "@/_helpers/swal";
import UploadImage from "@/components/forms/UploadImage.vue";
import {ICategory} from "@/models/category/ICategory";
import {IPostCategoryPlayload} from "@/models/category/ICategory";
import {ICategoryForm, IparentValue} from "@/models/category/ICategoryForm";
import {fetchCategory} from "@/_services/category/category";

const title = ref<string>("Création de catégorie");
const submitBtnLabel = 'Créer';
const route = useRoute();
const router = useRouter();
const loading = ref<boolean>(true);
const isAddSubCategory = ref<boolean>(false);
const parentUuidRouteParam = ref<string>("");

onMounted(async () => {
  await getCategoriesParentsChoiceListLabels();
  parentUuidRouteParam.value = route.params.uuid ? route.params.uuid as string : "";
  isAddSubCategory.value = parentUuidRouteParam.value.length ? true : false;
  if (isAddSubCategory.value) {
    title.value = 'Création de sous-catégorie';
    loading.value = false;
    await hydrateFormParentField(parentUuidRouteParam.value);
  }
  loading.value = false;
});

const form = ref<ICategoryForm>({
  name: '',
  slug: '',
  description: '',
  parent: null,
  parentValue: null,
  enabled: true,
  vignette: null,
  icon3d: null,  
});

const hydrateFormParentField = (uuid: string) => {
  fetchCategory(uuid)
    .then((response: ICategory) => {
      const matchingParent = parents.value.find(parent => parent.__original.uuid === response.uuid);
      if (matchingParent) {
        let preSelectedParent: IparentValue = { 
          uuid: matchingParent.__original.uuid,
          lvl: matchingParent.__original.lvl
        };
        form.value.parentValue = preSelectedParent;
      }
    })
    .catch((error) => {
      swal.error("une erreur est survenue lors de la mise à jour du formulaire avec les infos de la catégorie parente.");
    });
}

const parents = ref<Array<any>>([]);
const getCategoriesParentsChoiceListLabels = () => {
  const maxParentDropDownItemsPerPage = 500;

  return new Promise<void>((resolve, reject) => {
    ApiService.get('categories', {
      params: { itemsPerPage: maxParentDropDownItemsPerPage },
      headers: { Accept: "application/json" },
    })
    .then((response) => {
      const data = response.data.data;
      const categories = flattenChildren(data);
      categories[0].label = "Aucune";
      parents.value = categories;
      resolve();
    })
    .catch((error) => {
      reject(error);
    });
  });
}
function getBaseUrl() {
  return window.location.origin;
}
function flattenChildren(arr: any[]): Array<any> {
  const result: Array<any> = [];
  const stack = [...arr];
  while (stack.length > 0) {
    const category = stack.shift();
    if (category.archived || category.lvl === 4) continue;
    result.push({
      value: {
        uuid: category.uuid,
        lvl: category.lvl,
      },
      label: "-".repeat(category.lvl) + category.name,
    });
    /*if (category.children.length > 0) {
      stack.unshift(...category.children);
    }*/
  }
  return [...new Set(result)];
}

const onSubmit = async (formData, formNode) => {
  try {
    const payload: IPostCategoryPlayload = {
      name: formData.name,
      slug: formData.slug,
      description: formData.description,
      enabled: formData.status,
      parent: null !== formData.parentValue ? `/api/categories/${formData.parentValue.uuid}` : null,
      vignette: null !== form.value.vignette ? `/api/images/${form.value.vignette.uuid}` : null, 
        icon3d: null !== form.value.icon3d ? `/api/images/${form.value.icon3d.uuid}` : null
    }
    swal.confirm(
      `Êtes-vous sûr de vouloir créer cette catégorie ?`,
      "Annuler",
      submitBtnLabel
    )
    .then((results) => results.isConfirmed && createCategorie(payload))
  } catch (err: any) {
    swal.error(err.errors[0]);
  }
}

const createCategorie = (data: IPostCategoryPlayload) => {
  ApiService.post('categories', data)
    .then((response) => {
      swal.info("La catégorie "+ response.data.data.name +" a été créée avec succès")
        .then((result) => {
          if (result.isConfirmed) {
            router.push({ name: 'listCategories' });
          }
          form.value.parentValue?.lvl === 0 
            ? router.push({ name: "listCategories" })
            : router.push({
              name: "listCategories",
              params: { uuid: form.value.parentValue?.uuid },
            });
      });
    })
    .catch((error) => {
      swal.error("Une erreur est survenue lors de la création de la catégorie : " + error.response.data.message);
    });
}

const onCancel = () => {
  swal.confirm("Êtes-vous sûr de vouloir annuler ?", "Oui", "Non")
    .then((result) => {
    if (result.isDismissed) {
      router.push({ name: 'listCategories' });
    }
  });
}

const onImageUpload = (image) => {
  form.value.vignette = image;
}

const onImageRemoved = () => {
  form.value.vignette = null;
}

const onImageUpload3d = (image) => {
    form.value.icon3d = image;
}

const onImageRemoved3d = () => {
    form.value.icon3d = null;
}

const onImageUploadError = (errorMsg: string) => {
  swal.error(errorMsg);
}
</script>